import React, { useState, useRef, useEffect, useMemo } from 'react';
import { showToast } from './ui/Toast';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import {
  LogOut,
  ChevronDown,
  Bell,
  Info,
  Users,
  User as UserCircle,
  Layers,
  Sparkles,
} from 'lucide-react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { useElderCare } from '../contexts/ElderCareContext';
import { DailyBriefPanel } from './DailyBriefPanel';
import { useFeatureAccess } from '../hooks/useFeatureAccess';

import { UserProfile, SafetyMode, Household, AppAction, LayoutNotification, DataScope } from '@/types';
import { FeatureDef, SubscriptionTier, FEATURE_REGISTRY } from '@everyday-os/shared';
import { db } from '@/firebase';
import { doc, updateDoc, arrayUnion } from 'firebase/firestore';
import { Sidebar } from './nav/Sidebar';
import { BottomTabBar } from './nav/BottomTabBar';
import { BottomSheet } from './nav/BottomSheet';
import { NAV_CATEGORIES } from './nav/NavCategories';

const THEME_OPTIONS = [
  { id: 'light',    label: 'Light',    bg: '#ffffff', accent: '#6b21a8' },
  { id: 'dark',     label: 'Dark',     bg: '#0f172a', accent: '#818cf8' },
  { id: 'midnight', label: 'Midnight', bg: '#000000', accent: '#7c3aed' },
  { id: 'ocean',    label: 'Ocean',    bg: '#042f2e', accent: '#2dd4bf' },
  { id: 'forest',   label: 'Forest',   bg: '#052e16', accent: '#34d399' },
  { id: 'sunset',   label: 'Sunset',   bg: '#fff7ed', accent: '#fb923c' },
] as const;

interface LayoutProps {
  children: React.ReactNode;
  user: UserProfile;
  theme: string;
  onToggleTheme: () => void;
  onSetTheme: (theme: string) => void;
  currentHousehold?: Household;
  notifications: LayoutNotification[];
  dispatch: React.Dispatch<AppAction>;
  onLogout: () => void;
}

export const Layout: React.FC<LayoutProps> = ({
  children,
  user,
  theme,
  onToggleTheme,
  onSetTheme,
  currentHousehold,
  notifications = [],
  dispatch,
  onLogout
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
  const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);
  const [isBriefOpen, setIsBriefOpen] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const profileMenuRef = useRef<HTMLDivElement>(null);
  const notificationsRef = useRef<HTMLDivElement>(null);

  const isChildMode = user.safetyMode === SafetyMode.STRICT;
  const { careProfiles } = useElderCare();

  const householdTier = currentHousehold?.subscription?.tier ?? SubscriptionTier.FREE;
  const { visibleNavFeatures } = useFeatureAccess(
    householdTier,
    user.hiddenFeatures ?? [],
    {
      householdId: currentHousehold?.id,
      householdModules: (currentHousehold as any)?.modules ?? [],
    }
  );

  const filteredVisibleFeatures = isChildMode
    ? visibleNavFeatures.filter(f => ['/dashboard', '/tasks', '/calendar'].includes(f.path))
    : visibleNavFeatures;

  const getPageTitle = () => {
    const allPaths = [
      ...FEATURE_REGISTRY,
      { id: 'settings', label: t('nav.settings', 'Household'), path: '/settings' },
    ];
    const current = allPaths.find(item => item.path === location.pathname);
    if (location.pathname === '/bug-report') return t('nav.reportBug', 'Report Bug');
    if (location.pathname === '/user-settings') return t('nav.userSettings', 'User Settings');
    if (location.pathname === '/contacts') return t('contacts.title', 'Directory');
    if (location.pathname === '/changelog') return "Protocol History";
    if (location.pathname === '/influencer') return t('nav.referral', 'Refer a Friend');
    if (location.pathname === '/feedback') return t('nav.feedback', 'Feedback & Polls');
    if (location.pathname === '/explore') return 'Explore';
    if (location.pathname === '/achievements') return 'Achievements';
    if (location.pathname === '/municipal-services') return 'Municipal Services';
    if (location.pathname === '/upgrade') return 'Upgrade';
    if (location.pathname === '/elder-care') return 'Elder Care';
    if (location.pathname === '/co-parenting') return 'Co-Parenting';
    return current ? current.label : t('nav.dashboard', 'Dashboard');
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (profileMenuRef.current && !profileMenuRef.current.contains(event.target as Node)) {
        setIsProfileMenuOpen(false);
      }
      if (notificationsRef.current && !notificationsRef.current.contains(event.target as Node)) {
        setIsNotificationsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const unreadNotifications = notifications.filter(n => !n.readBy?.includes(user.id));
  const unreadCount = unreadNotifications.length;

  const markRead = async (id: string) => {
    if (!currentHousehold) return;
    const nRef = doc(db, "households", currentHousehold.id, "notifications", id);
    try {
      await updateDoc(nRef, { readBy: arrayUnion(user.id) });
    } catch (e) { console.error("Error marking notification read:", e); }
  };

  const handleMarkAllRead = async () => {
    try {
      await Promise.all(unreadNotifications.map(n => markRead(n.id)));
    } catch (e) { console.error("Error marking all read:", e); }
  };

  const isDashboardPage = useMemo(() => location.pathname === '/dashboard', [location.pathname]);

  const handleRefreshBrief = async () => {
    setIsRefreshing(true);
    try {
      const fns = getFunctions();
      const refresh = httpsCallable(fns, 'refreshDailyBrief');
      await refresh({});
    } catch (e) {
      console.error('Brief refresh failed:', e);
    } finally {
      setIsRefreshing(false);
    }
  };

  const currentScope = user.defaultScope || 'BOTH';
  const handleScopeChange = (scope: DataScope) => {
    if (!currentHousehold && scope !== 'PERSONAL') {
        showToast(t('scope.no_household_alert', 'You must be part of a household to use Shared or Hybrid modes.'), 'error');
        return;
    }
    dispatch({ type: 'SET_DATA_SCOPE', payload: scope });
    if (user.id) {
        updateDoc(doc(db, "users", user.id), { defaultScope: scope }).catch(e => console.error("Failed to save defaultScope:", e));
    }
  };

  const [isBottomSheetOpen, setIsBottomSheetOpen] = useState(false);

  const visibleItemIds = useMemo(
    () => new Set(filteredVisibleFeatures.map((f: FeatureDef) => f.id)),
    [filteredVisibleFeatures]
  );

  const conditionalFlags = useMemo(() => ({
    hasCoParenting: !!currentHousehold?.coParentingLinkId,
    hasElderCare: careProfiles.length > 0,
    hasMunicipal: ((currentHousehold as any)?.subscribedMunicipalityIds?.length ?? 0) > 0,
  }), [currentHousehold, careProfiles]);

  const activeRouteCategoryId = useMemo(() => {
    for (const cat of NAV_CATEGORIES) {
      if (cat.directPath && location.pathname === cat.directPath) return cat.id;
      if (cat.items.some(item =>
        item.path === location.pathname ||
        (item.path !== '/dashboard' && location.pathname.startsWith(item.path))
      )) return cat.id;
    }
    return null;
  }, [location.pathname]);

  return (
    <>
      <div className="h-screen overflow-hidden flex flex-row transition-colors duration-300" style={{ backgroundColor: 'var(--theme-bg)' }}>

        {/* Desktop: sidebar */}
        <div className="hidden md:block flex-shrink-0">
          <Sidebar
            visibleItemIds={visibleItemIds}
            conditionalFlags={conditionalFlags}
            currentHousehold={currentHousehold}
            user={user}
            onLogout={onLogout}
          />
        </div>

        {/* Main content area */}
        <div className="flex-1 flex flex-col overflow-hidden">
          <header className="h-20 flex items-center justify-between px-8 bg-white/80 dark:bg-slate-950/80 backdrop-blur-md border-b border-slate-200/50 dark:border-slate-800/50 z-50">
            <h1 className="text-2xl font-black text-slate-950 dark:text-white tracking-tight italic uppercase">{getPageTitle()}</h1>

            <div className="flex items-center gap-6">
              {currentHousehold && (
                <div className="hidden lg:flex items-center bg-slate-100 dark:bg-slate-800 p-1 rounded-2xl border border-slate-200 dark:border-slate-700">
                  <button onClick={() => handleScopeChange('PERSONAL')} className={clsx("flex items-center gap-2 px-4 py-2 rounded-xl text-2xs font-black uppercase tracking-widest transition-all", currentScope === 'PERSONAL' ? "bg-white dark:bg-slate-900 text-brand shadow-sm" : "text-slate-400 hover:text-slate-600")}>
                    <UserCircle className="w-3 h-3" /> {t('common.personal', 'Personal')}
                  </button>
                  <button onClick={() => handleScopeChange('BOTH')} className={clsx("flex items-center gap-2 px-4 py-2 rounded-xl text-2xs font-black uppercase tracking-widest transition-all", currentScope === 'BOTH' ? "bg-white dark:bg-slate-900 text-brand shadow-sm" : "text-slate-400 hover:text-slate-600")}>
                    <Layers className="w-3 h-3" /> {t('common.hybrid', 'Hybrid')}
                  </button>
                  <button onClick={() => handleScopeChange('SHARED')} className={clsx("flex items-center gap-2 px-4 py-2 rounded-xl text-2xs font-black uppercase tracking-widest transition-all", currentScope === 'SHARED' ? "bg-white dark:bg-slate-900 text-brand shadow-sm" : "text-slate-400 hover:text-slate-600")}>
                    <Users className="w-3 h-3" /> {t('common.shared', 'Shared')}
                  </button>
                </div>
              )}

              <div className="flex items-center gap-3">
                <button onClick={() => setIsBriefOpen(true)} className="p-2.5 rounded-xl border transition-all bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700 text-slate-500 hover:text-brand hover:border-brand/30" title="Daily Brief">
                  <Sparkles className="w-5 h-5" />
                </button>

                <div className="relative" ref={notificationsRef}>
                  <button onClick={() => setIsNotificationsOpen(!isNotificationsOpen)} className={`p-2.5 rounded-xl border transition-all relative ${isNotificationsOpen ? 'bg-brand/10 dark:bg-brand/10 border-brand/30 text-brand' : 'bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700 text-slate-500'}`}>
                    <Bell className="w-5 h-5" />
                    {unreadCount > 0 && (<span className="absolute -top-1 -right-1 flex h-4 w-4 items-center justify-center rounded-full bg-rose-500 text-[9px] font-black text-white border-2 border-white dark:border-slate-900">{unreadCount}</span>)}
                  </button>
                  {isNotificationsOpen && (
                    <div className="absolute right-0 mt-3 w-80 bg-white dark:bg-slate-900 rounded-[2rem] shadow-2xl border border-slate-200 dark:border-slate-700 z-[100] overflow-hidden animate-in fade-in slide-in-from-top-2">
                      <div className="p-4 border-b border-slate-100 dark:border-slate-800 flex justify-between items-center bg-slate-50 dark:bg-slate-800/50">
                        <h3 className="font-black text-2xs uppercase text-slate-400 tracking-widest">Alert Ledger</h3>
                        {unreadCount > 0 && <button onClick={handleMarkAllRead} className="text-2xs font-black text-brand hover:underline">Clear All</button>}
                      </div>
                      <div className="max-h-80 overflow-y-auto p-2 space-y-1 no-scrollbar">
                        {notifications.length === 0 ? (
                          <div className="py-12 text-center text-slate-400 text-xs font-bold uppercase tracking-widest italic opacity-50">Zero Signal Found</div>
                        ) : (
                          notifications.map(n => (
                            <div key={n.id} className={`p-4 rounded-2xl transition-colors ${n.readBy?.includes(user.id) ? 'opacity-50' : 'bg-slate-50 dark:bg-slate-800 border border-slate-100 dark:border-slate-700'}`}>
                              <div className="flex items-center gap-2 mb-1">
                                <Info className="w-3 h-3 text-brand" />
                                <p className="text-xs font-black text-slate-800 dark:text-slate-200">{n.title}</p>
                              </div>
                              <p className="text-2xs font-medium text-slate-500 dark:text-slate-400 leading-relaxed">{n.message}</p>
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                  )}
                </div>

                <div className="relative" ref={profileMenuRef}>
                  <button onClick={() => setIsProfileMenuOpen(!isProfileMenuOpen)} className="flex items-center gap-3 p-1 rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 hover:shadow-md transition-all">
                    <img src={user.avatar || `https://ui-avatars.com/api/?name=${user.name}`} className="w-9 h-9 rounded-xl object-cover" alt="profile" />
                    <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform hidden md:block ${isProfileMenuOpen ? 'rotate-180' : ''}`} />
                  </button>
                  {isProfileMenuOpen && (
                    <div className="absolute right-0 mt-3 w-64 bg-white dark:bg-slate-900 rounded-[1.5rem] shadow-2xl border border-slate-200 dark:border-slate-700 py-2 z-[100] animate-in fade-in zoom-in-95">
                      <div className="px-5 py-3 border-b border-slate-100 dark:border-slate-800 mb-2">
                        <p className="text-xs font-black text-slate-900 dark:text-white truncate">{user.name}</p>
                        <p className="text-2xs font-bold text-slate-400 uppercase tracking-widest">{user.role}</p>
                      </div>
                      <div className="px-5 py-2.5">
                        <p className="text-2xs font-bold text-slate-400 uppercase tracking-widest mb-2">Theme</p>
                        <div className="grid grid-cols-3 gap-1.5">
                          {THEME_OPTIONS.map(t => (
                            <button key={t.id} onClick={() => { onSetTheme(t.id); setIsProfileMenuOpen(false); }} title={t.label} className={clsx('flex flex-col items-center gap-1 p-1.5 rounded-xl border-2 transition-all', theme === t.id ? 'border-primary-500 scale-105' : 'border-transparent hover:border-slate-300 dark:hover:border-slate-600')}>
                              <span className="w-7 h-7 rounded-full border border-black/10 flex-shrink-0" style={{ background: `linear-gradient(135deg, ${t.bg} 50%, ${t.accent} 50%)` }} />
                              <span className="text-2xs font-bold text-slate-500 dark:text-slate-400">{t.label}</span>
                            </button>
                          ))}
                        </div>
                      </div>
                      <button onClick={() => { navigate('/user-settings'); setIsProfileMenuOpen(false); }} className="w-full flex items-center gap-3 px-5 py-2.5 text-sm font-bold text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800">
                        <UserCircle className="w-4 h-4" /> {t('nav.account_settings', 'Account Settings')}
                      </button>
                      <div className="h-px bg-slate-100 dark:bg-slate-800 my-2" />
                      <button onClick={onLogout} className="w-full flex items-center gap-3 px-5 py-3 text-sm font-black text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-900/20">
                        <LogOut className="w-4 h-4" /> Sign Out
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </header>

          <main className={clsx("flex-1 px-0 overflow-y-auto no-scrollbar pb-16 md:pb-0")}>
            <div className={clsx("pb-20", isDashboardPage ? 'pt-0' : 'md:py-8', !isDashboardPage && 'px-8')}>
              {children}
            </div>
          </main>
        </div>

        {/* Mobile: bottom tab bar */}
        <BottomTabBar
          categories={NAV_CATEGORIES}
          activeRouteCategoryId={activeRouteCategoryId}
          onTabClick={(catId) => {
            const cat = NAV_CATEGORIES.find(c => c.id === catId);
            if (!cat) return;
            if (cat.directPath) { navigate(cat.directPath); return; }
            const firstVisible = cat.items.find(item => !item.conditional && visibleItemIds.has(item.id));
            if (firstVisible) navigate(firstVisible.path);
          }}
          onMoreClick={() => setIsBottomSheetOpen(true)}
        />

        {/* Mobile: bottom sheet */}
        <BottomSheet
          isOpen={isBottomSheetOpen}
          categories={NAV_CATEGORIES}
          visibleItemIds={visibleItemIds}
          conditionalFlags={conditionalFlags}
          activeRoute={location.pathname}
          onNavigate={(path) => { navigate(path); setIsBottomSheetOpen(false); }}
          onClose={() => setIsBottomSheetOpen(false)}
        />
      </div>

      <DailyBriefPanel
        uid={user.id}
        isOpen={isBriefOpen}
        onClose={() => setIsBriefOpen(false)}
        onRefresh={handleRefreshBrief}
        isRefreshing={isRefreshing}
      />
    </>
  );
};